import React, { useRef, useState, useEffect } from "react";
import { Typography, Stack, Avatar } from "@mui/material";
import { brand } from "../../theme/themePrimitives";
import { useCustomisation } from "../../context/CustomisationContext";
import { useToast } from "../../context/ToastContext";
import { resizeImage } from "../../utils/imageHelpers";

interface ProfilePictureUploadProps {
  value?: string | File | null;
  onChange: (file: File | null) => void;
  maxWidth?: number;
  maxHeight?: number;
  accept?: string;
  helperText?: string;
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  value,
  onChange,
  maxWidth = 800,
  maxHeight = 400,
  helperText,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const { customisations } = useCustomisation();
  const { showToast } = useToast();

  const {
    PROFILE: { buttonsSettings: profilebuttonssettings },
    LOGIN: { buttonsSettings: loginButtonSettings },
  } = customisations.customisations;
  const linkTextColor =
    profilebuttonssettings?.profileLinkColor ??
    loginButtonSettings?.loginLinkColor;

  useEffect(() => {
    if (value instanceof File) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      const objectUrl = URL.createObjectURL(value);
      objectUrlRef.current = objectUrl;
      setPreviewUrl(objectUrl);
    } else if (typeof value === "string" && value) {
      setPreviewUrl(value);
    } else {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPreviewUrl(null);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const fileType = file.type.toLowerCase();
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    // Define valid MIME types and extensions
    const validMimeTypes = [
      "image/svg+xml",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
    ];
    const validExtensions = ["svg", "png", "jpg", "jpeg", "gif"];

    const isValidType =
      validMimeTypes.includes(fileType) ||
      (fileExtension && validExtensions.includes(fileExtension));

    if (!isValidType) {
      showToast(
        "Please select a valid image file. Accepted formats: SVG, PNG, JPG, or GIF",
        "error",
      );
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size must not exceed 5MB.", "error");
      e.target.value = "";
      return;
    }

    // For SVG files, skip dimension validation (vector graphics don't have fixed dimensions)
    const isSvg = fileType === "image/svg+xml" || fileExtension === "svg";

    if (isSvg) {
      onChange(file);
      return;
    }

    // Resize image if it exceeds max dimensions
    try {
      const resizedFile = await resizeImage(file, maxWidth, maxHeight);
      onChange(resizedFile);
    } catch (error) {
      console.error("Error resizing image:", error);
      showToast("Error processing image. Please try again.", "error");
    }
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  return (
    <Stack spacing={2} alignItems="center">
      {previewUrl && (
        <Avatar
          src={previewUrl}
          alt="Profile picture"
          sx={{
            width: 120,
            height: 120,
            border: `2px solid ${brand[200]}`,
          }}
        />
      )}
      {!previewUrl && (
        <Avatar
          sx={{
            width: 120,
            height: 120,
            border: `2px solid ${brand[200]}`,
            bgcolor: brand[100],
          }}
        />
      )}
      <Typography
        component="button"
        onClick={handleUploadClick}
        sx={{
          color: linkTextColor,
          cursor: "pointer",
          border: "none",
          background: "none",
          padding: 0,
          fontSize: "14px",
          fontWeight: 500,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Upload Photo
      </Typography>
      {helperText && (
        <Stack spacing={0.5} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            {helperText}
          </Typography>
        </Stack>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/svg+xml,image/png,image/jpeg,image/jpg,image/gif,.svg,.png,.jpg,.jpeg,.gif"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </Stack>
  );
};
